import {Injectable, signal} from '@angular/core';
import {BehaviorSubject, Observable, Subject} from 'rxjs';
import {UserService} from '../api/services/user.service';
import {UserAiSettings} from '../api/interfaces/user.interface';
import {isInIframe} from '../shared/helpers/maporium.validators';

@Injectable({
  providedIn: 'root'
})
export class AiStateService {
  private isOpenAIKeyValidSubject = new BehaviorSubject<boolean>(false);
  private selectedModelSubject = new BehaviorSubject<string>('');
  private aiSettings: UserAiSettings | null = null;
  private settingsLoaded: Promise<void>;
  private resolveSettingsLoaded!: () => void;

  // Channel for features outside the assistant (e.g. the legend panel's
  // GENERATE button) to submit a prompt on the user's behalf. The assistant
  // bottom sheet subscribes to this once it's open; callers are responsible
  // for opening the sheet first when it's closed.
  /**
   * MAP-147: the user's own switch for AI, separate from whether a key is
   * configured. Off means the product shows no AI at all; a generation already
   * running is left to finish.
   */
  public aiFeaturesVisible = signal<boolean>(true);

  private autoSubmitPromptSubject = new Subject<string>();
  public autoSubmitPrompt$: Observable<string> = this.autoSubmitPromptSubject.asObservable();

  public submitAssistantPrompt(prompt: string): void {
    this.autoSubmitPromptSubject.next(prompt);
  }

  constructor(private userService: UserService) {
    this.settingsLoaded = new Promise<void>(resolve => this.resolveSettingsLoaded = resolve);
    if (!isInIframe()) {
      this.initializeState();
    } else {
      this.resolveSettingsLoaded();
    }
  }

  private initializeState() {
    // Load AI settings asynchronously
    this.userService.getAiSettings().subscribe({
      next: (settings) => {
        this.aiSettings = settings;
        this.aiFeaturesVisible.set(settings?.aiFeaturesEnabled !== false);
        this.setOpenAIKeyValid(this.resolveAiEnabled(settings));
        this.resolveSettingsLoaded();
      },
      error: () => {
        this.setOpenAIKeyValid(false);
        this.resolveSettingsLoaded();
      }
    });
  }

  private hasAnyApiKey(settings: UserAiSettings): boolean {
    return !!(settings?.openaiKey || settings?.anthropicKey || settings?.geminiKey);
  }

  private resolveAiEnabled(settings: UserAiSettings): boolean {
    if (!settings) return false;
    if (!settings.useCustomApiKeys) return !!settings.platformKeyAvailable;
    return this.hasAnyApiKey(settings);
  }

  setOpenAIKeyValid(isValid: boolean) {
    this.isOpenAIKeyValidSubject.next(isValid);
  }

  setSelectedModel(model: string) {
    this.selectedModelSubject.next(model);
  }

  areAIFeaturesEnabled(): boolean {
    if (!this.aiSettings) return false;
    if (!this.aiFeaturesVisible()) return false;
    return this.resolveAiEnabled(this.aiSettings);
  }

  /** Whether the product should show its AI controls at all. */
  isAiVisible(): boolean {
    return this.aiFeaturesVisible();
  }

  setAiFeaturesVisible(visible: boolean): void {
    this.aiFeaturesVisible.set(visible);
    if (this.aiSettings) {
      this.aiSettings.aiFeaturesEnabled = visible;
    }
  }

  async waitForAIFeaturesEnabled(): Promise<boolean> {
    await this.settingsLoaded;
    return this.areAIFeaturesEnabled();
  }

  isUsingCustomKeys(): boolean {
    return !!this.aiSettings?.useCustomApiKeys;
  }

  refreshAiSettings() {
    this.settingsLoaded = new Promise<void>(resolve => this.resolveSettingsLoaded = resolve);
    this.initializeState();
  }
}
